import type { LucideIcon } from 'lucide-react';
import { Percent, Tag } from 'lucide-react';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CardAction, EffectCardAction } from '../../service/api';
import Coin from '../Coin';
import ItemCard from '../ItemCard';

type CardActionsListSize = 'xs' | 'sm' | 'md' | 'lg';
type CardActionsListDisplay = 'full' | 'compact';
type CardActionsListLayout = 'horizontal' | 'vertical';

interface CardActionsListProps {
  actions: CardAction[];
  /** 'full' shows icons + titles, 'compact' shows icons + amount only */
  display?: CardActionsListDisplay;
  /** Flex direction for the list */
  layout?: CardActionsListLayout;
  /** Controls icon and text sizing */
  size?: CardActionsListSize;
  className?: string;
}

/** Size config for the effect icon container (mirrors ItemCard pattern) */
const effectSizeConfig = {
  xs: {
    container: '1.5rem',
    icon: 12,
    fontSize: '0.6rem',
    badgeFont: '0.55rem',
  },
  sm: { container: '3rem', icon: 24, fontSize: '0.65rem', badgeFont: '0.6rem' },
  md: { container: '3rem', icon: 24, fontSize: '0.75rem', badgeFont: '0.7rem' },
  lg: {
    container: '4rem',
    icon: 32,
    fontSize: '0.875rem',
    badgeFont: '0.75rem',
  },
};

/** Maps Coin sizes to CardActionsList sizes — aligned with ItemCard container sizes */
const coinSizeMap = {
  xs: 'xs' as const,
  sm: 'md' as const,
  md: 'md' as const,
  lg: 'lg' as const,
};

/** Maps ItemCard sizes to CardActionsList sizes */
const itemSizeMap = {
  xs: 'sm' as const,
  sm: 'sm' as const,
  md: 'sm' as const,
  lg: 'md' as const,
};

/**
 * Formats the modifier string for an effect action.
 * Returns a display string like "= 50", "−20%", "+10%".
 */
function formatEffectValue(action: EffectCardAction): string {
  if (action.effectType === 'set_price') {
    return `= ${action.value}`;
  }
  // Percentage-based effects
  const sign = action.value > 0 ? '+' : '';
  return `${sign}${action.value}%`;
}

/**
 * Returns the background color class for an effect badge.
 */
function getEffectBadgeColor(action: EffectCardAction): string {
  if (action.effectType === 'set_price') {
    return 'bg-info';
  }
  // Negative percentage = discount (good), positive = price increase (bad)
  return action.value < 0 ? 'bg-success' : 'bg-danger';
}

/**
 * Returns the lucide icon component for a given effect type.
 */
function getEffectIcon(action: EffectCardAction): LucideIcon {
  if (action.effectType === 'set_price') {
    return Tag;
  }
  return Percent;
}

/**
 * Returns a human-readable label for the effect action.
 */
function getEffectLabel(
  action: EffectCardAction,
  t: (key: string) => string,
): string {
  switch (action.effectType) {
    case 'set_price':
      return action.targetItem
        ? `${t('effects.setPrice')}: ${action.targetItem.title}`
        : t('effects.setPrice');
    case 'adjust_all_prices_percent':
      return t('effects.adjustAllPrices');
    case 'adjust_item_price_percent':
      return action.targetItem
        ? `${t('effects.adjustItemPrice')}: ${action.targetItem.title}`
        : t('effects.adjustItemPrice');
    default:
      return t('effects.unknownEffect');
  }
}

/**
 * Renders a single effect action as an icon with an overlaid value badge,
 * following the same visual pattern as ItemCard and Coin.
 */
const EffectActionIcon: React.FC<{
  action: EffectCardAction;
  size: CardActionsListSize;
  showTitle: boolean;
}> = ({ action, size, showTitle }) => {
  const { t } = useTranslation();
  const config = effectSizeConfig[size];
  const IconComponent: LucideIcon = getEffectIcon(action);
  const badgeColor = getEffectBadgeColor(action);
  const valueText = formatEffectValue(action);

  return (
    <div className="d-flex flex-column align-items-center">
      <div className="position-relative">
        <div
          className="d-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-75"
          style={{
            width: config.container,
            height: config.container,
          }}
        >
          {action.targetItem ? (
            <DynamicIcon
              name={action.targetItem.icon}
              size={config.icon}
              strokeWidth={1.5}
              className="text-white"
            />
          ) : (
            <IconComponent
              size={config.icon}
              strokeWidth={1.5}
              className="text-white"
            />
          )}
        </div>
        <span
          className={`position-absolute top-100 start-100 translate-middle badge rounded-pill ${badgeColor} text-white`}
          style={{ fontSize: config.badgeFont, minWidth: '1.5rem' }}
        >
          {valueText}
        </span>
      </div>
      {showTitle && (
        <div
          className="mt-1 text-center text-white-50"
          style={{ fontSize: config.fontSize, maxWidth: config.container }}
        >
          {getEffectLabel(action, t)}
        </div>
      )}
    </div>
  );
};

/** Compact item icon for xs size — small icon container with amount badge */
const CompactItemIcon: React.FC<{
  item: { icon: IconName; title?: string };
  amount: number;
}> = ({ item, amount }) => (
  <div className="position-relative d-inline-block">
    <div
      className="d-flex align-items-center justify-content-center rounded-3 bg-primary"
      style={{ width: '1.5rem', height: '1.5rem' }}
    >
      <DynamicIcon
        name={item.icon}
        size={12}
        strokeWidth={1.5}
        className="text-white"
      />
    </div>
    <span
      className={`position-absolute badge rounded-pill text-white ${amount < 0 ? 'bg-danger' : 'bg-success'}`}
      style={{
        bottom: '-4px',
        right: '-8px',
        fontSize: '0.55rem',
      }}
    >
      {amount > 0 ? '+' : ''}
      {amount}
    </span>
  </div>
);

/** Compact effect badge for xs size — inline badge with value */
const CompactEffectBadge: React.FC<{ action: EffectCardAction }> = ({
  action,
}) => {
  const badgeColor = getEffectBadgeColor(action);
  const valueText = formatEffectValue(action);

  return (
    <span
      className={`badge ${badgeColor} d-inline-flex align-items-center`}
      style={{ fontSize: '0.7rem' }}
    >
      ⚡ {valueText}
    </span>
  );
};

/**
 * Universal component to display a list of card actions.
 *
 * Renders each action as an icon with an overlaid amount/value badge:
 * - Sticker actions → Coin component (star icon with amount)
 * - Item actions → ItemCard component (item icon with quantity badge)
 * - Effect actions → Custom icon with value badge (=50, −20%, +10%)
 *
 * Supports configurable display (full/compact), layout (horizontal/vertical),
 * and size (sm/md/lg).
 */
const CardActionsList: React.FC<CardActionsListProps> = ({
  actions,
  display = 'compact',
  layout = 'horizontal',
  size = 'md',
  className = '',
}) => {
  const showTitle = display === 'full';
  const flexDirection = layout === 'horizontal' ? 'flex-row' : 'flex-column';

  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <div
      className={`d-flex ${flexDirection} align-items-center gap-2 ${className}`}
    >
      {actions.map((action, idx) => {
        switch (action.type) {
          case 'sticker':
            return size === 'xs' ? (
              <span
                key={idx}
                className={`d-inline-flex align-items-center ${action.amount < 0 ? 'text-danger' : 'text-success'}`}
              >
                <span className="fw-bold">
                  {action.amount > 0 ? '+' : ''}
                  {action.amount}
                </span>
                <Coin size="xs" className="ms-1" />
              </span>
            ) : (
              <div key={idx} className="d-flex flex-column align-items-center">
                <Coin size={coinSizeMap[size]}>
                  <span>{action.amount}</span>
                </Coin>
                {showTitle && <StickerLabel size={size} />}
              </div>
            );

          case 'item':
            if (!action.item) return null;
            return size === 'xs' ? (
              <CompactItemIcon
                key={idx}
                item={action.item}
                amount={action.amount}
              />
            ) : (
              <ItemCard
                key={idx}
                item={action.item}
                quantity={action.amount}
                size={itemSizeMap[size]}
                showPrice={false}
                showTitle={showTitle}
              />
            );

          case 'effect':
            return size === 'xs' ? (
              <CompactEffectBadge key={idx} action={action} />
            ) : (
              <EffectActionIcon
                key={idx}
                action={action}
                size={size}
                showTitle={showTitle}
              />
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

/** Small helper to render the "Stickers" label below Coin in full display mode */
const StickerLabel: React.FC<{ size: CardActionsListSize }> = ({ size }) => {
  const { t } = useTranslation();
  const fontSize = effectSizeConfig[size].fontSize;

  return (
    <div className="mt-1 text-center text-white-50" style={{ fontSize }}>
      {t('common.stickers')}
    </div>
  );
};

export default CardActionsList;
export type {
  CardActionsListDisplay,
  CardActionsListLayout,
  CardActionsListProps,
  CardActionsListSize,
};
